import type { ViewProps } from 'react-native';
import type { IconName } from '@sg/garnish';

import { TierName } from '@order/graphql';

import { BRAINTREE_CREDIT_CARDS_ICONS } from '../../../../SweetpassCheckoutScreen/SweetpassCheckoutCardBlock';
import type { AccountSweetpassPaymentMethodsSection } from '../../useSweetpassAccountContentfulData';
import type { GetSectionProps, SweetpassSectionProps } from '../types';

export const getPaymentMethodsSection = (
  props: GetPaymentMethodsSectionProps,
): SweetpassPaymentMethodsSectionProps | undefined => {
  const {
    name,
    paymentMethods,
    paymentMethodsDataTitle,
    tier,
    viewPaymentMethodHandler,
    addPaymentMethodHandler,
    formatMessage,
  } = props;

  if (tier?.name === TierName.OptedOut || !paymentMethods) {
    return undefined;
  }

  return {
    name,
    title: paymentMethodsDataTitle,
    items: paymentMethods.map((paymentMethod) => {
      const icon = BRAINTREE_CREDIT_CARDS_ICONS[paymentMethod.cardType];
      const title = formatMessage(
        'account.sweetpass-membership.payment-methods.item.card-ending-in',
        {
          cardType: paymentMethod.cardType,
          lastFour: paymentMethod.lastFour,
        },
      );
      const tag = paymentMethod.isActive
        ? formatMessage(
            'account.sweetpass-membership.payment-methods.item.tag.active',
          )
        : undefined;

      const accessibilityLabel = [title, tag].filter(Boolean).join('; ');

      const accessibilityHint = formatMessage(
        'account.sweetpass-membership.payment-methods.item.accessibility-hint',
      );

      return {
        id: paymentMethod.id,
        isActive: paymentMethod.isActive,
        icon,
        title,
        accessibilityLabel,
        accessibilityHint,
        tag,
        handler() {
          viewPaymentMethodHandler(paymentMethod.id);
        },
      };
    }),
    addItem: {
      icon: 'IconAdd',
      title: formatMessage(
        'account.sweetpass-membership.payment-methods.add-item.title',
      ),
      accessibilityLabel: formatMessage(
        'account.sweetpass-membership.payment-methods.add-item.accessibility-label',
      ),
      accessibilityHint: formatMessage(
        'account.sweetpass-membership.payment-methods.add-item.accessibility-hint',
      ),
      handler: addPaymentMethodHandler,
    },
  };
};

type GetPaymentMethodsSectionProps = {
  readonly viewPaymentMethodHandler: (paymentMethodId: string) => void;
  readonly addPaymentMethodHandler: () => void;
} & AccountSweetpassPaymentMethodsSection &
  GetSectionProps;

type SweetpassPaymentMethodItem = {
  readonly id: string;
  readonly icon: IconName;
  readonly title?: string;
  readonly subtitle?: string;
  readonly notice?: string;
  readonly tag?: string;
  readonly accessibilityLabel: string;
  readonly accessibilityHint: string;
  readonly handler: () => void;
} & ViewProps;

type SweetpassPaymentMethodAddItem = {
  readonly icon: IconName;
  readonly title: string;
  readonly accessibilityLabel: string;
  readonly accessibilityHint: string;
  readonly handler: () => void;
} & ViewProps;

export type SweetpassPaymentMethodsSectionProps = {
  readonly name: string;
  readonly title: string;
  readonly items: readonly SweetpassPaymentMethodItem[];
  readonly addItem: SweetpassPaymentMethodAddItem;
} & SweetpassSectionProps;
